<!--
  Messaggi — WhatsApp-style chat (route: /messaggi)
  Desktop only (no media queries). Two-column layout:
    - left  : sidebar with toggle + search + list of conversations
    - right : header of the active conversation + message stream + composer

  Audience: admin / super-admin. Operators write/read messages from the
  order detail screen (same service backend, different page).
-->

<div class="chat-page">

  <!-- ─── Top header (sticky) ────────────────────────────────────── -->
  <header class="chat-header" role="banner">
    <div class="chat-header__brand">
      <h1 class="chat-header__title">💬 Messaggi</h1>
      <p class="chat-header__subtitle">Conversazioni operatore ↔ supervisore</p>
    </div>

    <div class="chat-header__toggle" role="tablist" aria-label="Modalità di raggruppamento">
      <button
        type="button"
        role="tab"
        class="toggle-pill"
        [class.toggle-pill--active]="mode === 'contacts'"
        [attr.aria-selected]="mode === 'contacts'"
        (click)="setMode('contacts')"
      >
        Contatti
      </button>
      <button
        type="button"
        role="tab"
        class="toggle-pill"
        [class.toggle-pill--active]="mode === 'orders'"
        [attr.aria-selected]="mode === 'orders'"
        (click)="setMode('orders')"
      >
        Ordini
      </button>
    </div>
  </header>

  <!-- ─── Body: two-column layout ────────────────────────────────── -->
  <div class="chat-body">

    <!-- ── Sidebar ────────────────────────────────────────────── -->
    <aside class="chat-sidebar" aria-label="Elenco conversazioni">
      <div class="chat-sidebar__search">
        <input
          type="search"
          class="search-input"
          [placeholder]="mode === 'orders' ? 'Cerca per ordine…' : 'Cerca per nome…'"
          [(ngModel)]="searchText"
          aria-label="Cerca conversazione"
        />
      </div>

      <div class="chat-sidebar__list" role="list">
        @if (loadingSidebar && conversations.length === 0) {
          <!-- Skeleton during first load. -->
          @for (i of [1, 2, 3, 4, 5]; track i) {
            <div class="conv-skeleton" aria-busy="true">
              <div class="conv-skeleton__avatar"></div>
              <div class="conv-skeleton__body">
                <div class="conv-skeleton__line conv-skeleton__line--lg"></div>
                <div class="conv-skeleton__line conv-skeleton__line--sm"></div>
              </div>
            </div>
          }
        } @else if (filteredConversations.length === 0) {
          <div class="conv-empty">
            <div class="conv-empty__icon" aria-hidden="true">📭</div>
            <p class="conv-empty__msg">
              @if (searchText) {
                Nessun risultato per "{{ searchText }}".
              } @else if (mode === 'orders') {
                Nessuna conversazione collegata a un ordine.
              } @else {
                Nessuna conversazione.
              }
            </p>
          </div>
        } @else {
          @for (conv of filteredConversations; track conv.message_id) {
            <button
              type="button"
              role="listitem"
              class="conv-item"
              [class.conv-item--active]="selectedConversation?.message_id === conv.message_id"
              [class.conv-item--unread]="isUnread(conv)"
              (click)="open(conv)"
            >
              <div class="conv-item__avatar" [attr.data-mode]="mode">
                {{ initials(conv) }}
              </div>

              <div class="conv-item__body">
                <div class="conv-item__top">
                  <span class="conv-item__title">
                    @if (mode === 'orders') {
                      {{ orderLabel(conv) }}
                    } @else {
                      {{ contactLabel(conv) }}
                    }
                  </span>
                  <span class="conv-item__time">{{ formatTime(conv.ms_datetime) }}</span>
                </div>

                <div class="conv-item__bottom">
                  <span class="conv-item__preview">{{ conv.ms_title || conv.ms_message || '…' }}</span>
                  @if (isUnread(conv)) {
                    <span class="conv-item__dot" aria-label="Non letto"></span>
                  }
                </div>
              </div>
            </button>
          }
        }
      </div>
    </aside>

    <!-- ── Main chat area ──────────────────────────────────────── -->
    <section class="chat-main" aria-label="Conversazione attiva">

      @if (!selectedConversation) {
        <!-- Empty state when no conversation has been opened yet. -->
        <div class="chat-empty">
          <div class="chat-empty__icon" aria-hidden="true">💬</div>
          <h2 class="chat-empty__title">Seleziona una conversazione</h2>
          <p class="chat-empty__msg">
            Scegli un {{ mode === 'orders' ? 'ordine' : 'contatto' }} dalla colonna
            a sinistra per visualizzare i messaggi.
          </p>
        </div>
      } @else {
        <!-- Header of the active conversation -->
        <header class="chat-thread-header">
          <div class="chat-thread-header__avatar" [attr.data-mode]="mode">
            {{ initials(selectedConversation) }}
          </div>
          <div class="chat-thread-header__text">
            <h2 class="chat-thread-header__title">{{ activeTitle }}</h2>
            <p class="chat-thread-header__subtitle">{{ activeSubtitle }}</p>
          </div>
        </header>

        <!-- Message stream -->
        <div #messagesScroll class="chat-thread" aria-live="polite">
          @if (loadingMessages && !hasMessages) {
            <!-- Skeleton bubbles during first messages fetch. -->
            <div class="bubble-skeleton bubble-skeleton--received" aria-busy="true"></div>
            <div class="bubble-skeleton bubble-skeleton--sent" aria-busy="true"></div>
            <div class="bubble-skeleton bubble-skeleton--received bubble-skeleton--lg" aria-busy="true"></div>
          } @else if (!hasMessages) {
            <div class="thread-empty">
              <div class="thread-empty__icon" aria-hidden="true">✉️</div>
              <p class="thread-empty__msg">Nessun messaggio in questa conversazione. Scrivi il primo!</p>
            </div>
          } @else {
            @for (msg of selectedMessages; track msg.ms_datetime + msg.ms_message; let last = $last; let idx = $index) {
              <!--
                🚨 v2.12.0 L.4: date separator WhatsApp-style sopra il
                primo messaggio della giornata. dayLabel ritorna "Oggi"
                / "Ieri" / "Giovedì" / "19/06/2026" a seconda della
                distanza dal current day.
              -->
              @if (showDateSeparator(idx)) {
                <div class="day-separator" role="separator">
                  <span class="day-separator__label">{{ dayLabel(msg.ms_datetime) }}</span>
                </div>
              }
              @let side = bubbleSide(msg);
              <div class="bubble-row" [attr.data-side]="side">
                <div class="bubble" [class.bubble--sent]="side === 'sent'" [class.bubble--received]="side === 'received'">
                  @if (msg.ms_title && msg.ms_title !== msg.ms_message) {
                    <div class="bubble__title">{{ msg.ms_title }}</div>
                  }
                  <div class="bubble__text">{{ msg.ms_message }}</div>
                  <div class="bubble__meta">
                    <span class="bubble__time">{{ formatTime(msg.ms_datetime) }}</span>
                    @if (side === 'sent') {
                      <!--
                        Per-message read status is not in the model
                        (IConversationMessage has no ms_status), so the
                        double-blue tick is rendered ONLY on the last sent
                        message and reflects the *conversation*-level
                        ms_status. See component header for the rationale.
                      -->
                      @if (last && lastMessageReadByPeer) {
                        <span class="bubble__ticks bubble__ticks--read" aria-label="Letto">✓✓</span>
                      } @else {
                        <span class="bubble__ticks" aria-label="Consegnato">✓</span>
                      }
                    }
                  </div>
                </div>
              </div>
            }
          }
        </div>

        <!-- Composer -->
        <div class="chat-composer">
          <textarea
            class="composer-input"
            rows="1"
            placeholder="Scrivi un messaggio…"
            [(ngModel)]="draft"
            (keydown.enter)="$event.preventDefault(); send()"
            [disabled]="sending"
            aria-label="Nuovo messaggio"
          ></textarea>
          <button
            type="button"
            class="composer-send"
            (click)="send()"
            [disabled]="sending || !draft.trim()"
            aria-label="Invia"
          >
            @if (sending) {
              <span class="composer-send__spinner" aria-hidden="true"></span>
            } @else {
              ▶
            }
          </button>
        </div>
      }
    </section>

  </div>
</div>
